// `Modal` (design-system spec §3.1 chrome / §3.8) — the ONE overlay primitive
// for the office areas.
//
// WHY IT EXISTS (surface review F5, 2026-08-12). The work-order create/release
// modal was a `role="dialog"` div inside a scrim div and nothing else:
//
//   * Escape did nothing;
//   * clicking the scrim did nothing;
//   * there was no close control at all — the only exits were the two step
//     buttons ("Cancel" on step 1, "Release later" on step 2), so a planner who
//     opened it from the S2 header and changed their mind had to guess which
//     of two differently-worded buttons meant "get me out";
//   * focus never entered the panel, was never trapped, and was never
//     restored, so a keyboard user tabbed straight from the dialog into the
//     page BEHIND the scrim and operated a list they could not see.
//
// The onboarding area had already solved this correctly in
// `areas/onboarding/components/wizard-dialog.tsx` (its own header records the
// same four defects, found in UX-1) — so this is not a new mechanism, it is
// that mechanism moved into the design layer where every area inherits it
// instead of the next modal re-discovering the list. `WizardDialog` keeps its
// own confirmation grammar (title + confirm/cancel actions) and is a CONSUMER
// shape, not a duplicate: the trap logic below is the shared part.
//
// WHAT IT GUARANTEES
//  * `role="dialog"` + `aria-modal` + an accessible name — the name comes from
//    the visible heading when there is one, and from `label` when there is not;
//  * focus moves INTO the panel on open and is restored to the opener on
//    close (the control the user pressed is where their focus belongs);
//  * Tab and Shift+Tab cycle inside the panel;
//  * Escape closes, the scrim closes, and a labelled ✕ closes. Closing is
//    always the SAFE outcome: this primitive is for panels whose dismissal
//    loses nothing but typing. A destructive confirmation is a different shape
//    and belongs in an alertdialog with an explicit decision (WizardDialog).
//  * `pending` makes every exit INERT while the caller's mutation is in
//    flight (added 2026-09-08, from the catalogue withdraw review). A dialog
//    that fires a request and then lets Escape unmount it mid-flight leaves
//    the caller holding a `reset()` mutation whose per-call callbacks never
//    fire: the request lands, the row changes, and the screen keeps showing
//    the state from before the request — a "Scheduled" chip and a Withdraw
//    button on a version that is now archived. Closing during a pending write
//    is not the safe outcome; it is the one exit that loses more than typing.
//    The ✕ is disabled (so it reads inert, not broken), and the panel carries
//    `aria-busy` for the duration.
//
// The panel takes the caller's `className` so an area keeps its own width and
// paint (`.production-modal`) without owning the overlay behaviour.

import { useEffect, useId, useRef, type ReactNode } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  /** Accessible name when no visible heading is given (or a fuller one). */
  label: string;
  /** Visible heading. When present it NAMES the dialog (`aria-labelledby`). */
  heading?: ReactNode;
  /** Localized label for the ✕ control — every modal has one. */
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
  /** The area's own panel class (width, padding, its descendant rules). */
  className?: string | undefined;
  testId?: string | undefined;
  /**
   * A mutation this dialog owns is in flight: Escape, the scrim and the ✕ do
   * nothing until it settles, and the ✕ is disabled. Defaults to false — an
   * existing caller is unchanged.
   */
  pending?: boolean | undefined;
}

export function Modal({
  label,
  heading,
  closeLabel,
  onClose,
  children,
  className,
  testId,
  pending = false,
}: ModalProps) {
  const headingId = useId();
  const panel = useRef<HTMLDivElement>(null);
  // Captured on mount, restored on unmount.
  const opener = useRef<HTMLElement | null>(null);
  // `onClose` in a ref so the key handler is bound ONCE: a caller that passes
  // an inline arrow (all of them do) would otherwise re-register the listener
  // on every render. `pending` rides the same way, for the same handler.
  const close = useRef(onClose);
  close.current = onClose;
  const busy = useRef(pending);
  busy.current = pending;

  useEffect(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // The first focusable control inside the panel, else the panel itself so a
    // screen reader reads the dialog rather than the page behind it.
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();
    return () => {
      opener.current?.focus();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        // Inert, not ignored: the dialog still owns the key while it is busy.
        if (busy.current) return;
        close.current();
        return;
      }
      if (event.key !== "Tab" || panel.current === null) return;
      const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  return (
    <div
      className="ds-modal-scrim"
      data-testid="modal-scrim"
      onClick={() => {
        if (pending) return;
        onClose();
      }}
    >
      <div
        ref={panel}
        className={className === undefined ? "ds-modal" : `ds-modal ${className}`}
        role="dialog"
        aria-modal="true"
        {...(heading === undefined ? { "aria-label": label } : { "aria-labelledby": headingId })}
        {...(pending ? { "aria-busy": true } : {})}
        tabIndex={-1}
        data-testid={testId}
        onClick={(event) => {
          // A click inside the panel is not a click on the scrim.
          event.stopPropagation();
        }}
      >
        <div className="ds-modal-head">
          {heading === undefined ? null : (
            <h2 id={headingId} className="ds-modal-title">
              {heading}
            </h2>
          )}
          <button
            type="button"
            className="ds-modal-close"
            aria-label={closeLabel}
            data-testid="modal-close"
            disabled={pending}
            onClick={() => {
              if (pending) return;
              onClose();
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
